import React, { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import styles from "../../../styles/home/Components/HeroSection.module.css";
import { FaYoutube, FaChevronDown } from "react-icons/fa";
import img1 from "../../../assets/images/image2.png";
import img2 from "../../../assets/images/image12.png";
import img3 from "../../../assets/images/image6.png";
import img4 from "../../../assets/images/image8.png"
import EventNotificationPopup from "../../../components/EventNotificationPopup";

const slides = [
  {
    image: img1,
    title: "Calvary Wide Fellowship Ministries",
    subtitle: '"I was glad when they said to me, \'Let us go to the house of the Lord.\'" — Psalm 122:1',
    eyebrow: "House of God",
    tagline: "Kingdom-Centered  ·  Spirit-Led",
  },
  {
    image: img2,
    title: "Experience the Miracle of Healing",
    subtitle: '"For I am the LORD who heals you." — Exodus 15:26',
    eyebrow: "Divine Healing",
    tagline: "Signs  ·  Wonders  ·  Power",
  },
  {
    image: img3,
    title: "Raising an Army of Intercessors",
    subtitle: '"The prayer of a righteous person is powerful and effective." — James 5:16',
    eyebrow: "Intercession & Prayer",
    tagline: "Pray Without Ceasing",
  },
  {
    image: img4,
    title: "Worship That Transforms Lives",
    subtitle: '"God is Spirit, and His worshipers must worship in Spirit and in truth." — John 4:24',
    eyebrow: "Transformative Worship",
    tagline: "Spirit  ·  Truth  ·  Glory",
  },
];

const STATS = [
  { target: 9,  suffix: "+",  label: "Years of Ministry" },
  { target: 10, suffix: "K+", label: "Lives Touched" },
  { target: 20, suffix: "+",  label: "Weekly Services" },
  { target: 9, suffix: "",   label: "Parishes" },
];

function StatCounter({ target, suffix, label, active, index }) {
  const [count, setCount] = useState(0);
  const rafRef = useRef(null);
  const delay = index * 180; // stagger each item by 180 ms

  useEffect(() => {
    if (!active) return;
    const duration = 2000;
    let startTime = null;
    let timeout = null;
    const run = () => {
      const animate = (ts) => {
        if (!startTime) startTime = ts;
        const progress = Math.min((ts - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(eased * target));
        if (progress < 1) {
          rafRef.current = requestAnimationFrame(animate);
        } else {
          setCount(target);
        }
      };
      rafRef.current = requestAnimationFrame(animate);
    };
    timeout = setTimeout(run, delay);
    return () => {
      clearTimeout(timeout);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, active, delay]);

  // Incrementally larger value text: base 1.35rem, +0.18rem per step
  const valueFontSize = `${1.35 + index * 0.18}rem`;

  return (
    <div className={styles.statItem} data-index={index}>
      <span className={styles.statValue} style={{ fontSize: valueFontSize }}>{count}{suffix}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}


export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [countActive, setCountActive] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setCountActive(true), 900);
    return () => clearTimeout(t);
  }, []);

  const handleScrollDown = () => {
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 1500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5500,
    arrows: false,
    fade: true,
    beforeChange: (_, newIndex) => {
      setCurrentSlide(newIndex);
      setAnimKey((k) => k + 1);
    },
  };

  const slide = slides[currentSlide];

  return (
    <>
      <section className={styles.heroSection}>

        {/* Background image slider */}
        <div className={styles.sliderContainer}>
          <Slider {...settings}>
            {slides.map((s, idx) => (
              <div key={idx}>
                <div className={styles.slide}>
                  <img
                    src={s.image}
                    alt={s.title}
                    className={styles.bgImage}
                    loading={idx === 0 ? "eager" : "lazy"}
                  />
                </div>
              </div>
            ))}
          </Slider>
        </div>

        {/* Layered overlays */}
        <div className={styles.overlayMain} />
        <div className={styles.overlayVignette} />
        <div className={styles.lightRays} />

        {/* Hero content — key re-mounts/re-animates on every slide change */}
        <div className={styles.heroContent} key={animKey}>

          {/* Eyebrow tag */}
          <div className={styles.eyebrowRow}>
            <span className={styles.eyebrowBadge}>
              <span className={styles.eyebrowDot} />
              {slide.eyebrow}
            </span>
          </div>

          {/* Tagline */}
          <p className={styles.tagline}>{slide.tagline}</p>

          <h1 className={styles.heroTitle}>{slide.title}</h1>

          <div className={styles.dividerLine} />

          <p className={styles.heroSubtitle}>{slide.subtitle}</p>

          <div className={styles.heroActions}>
            <a
              href="https://www.youtube.com/@CMINTERNATIONAL"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.liveBtn}
              aria-label="Stream live on YouTube"
            >
              <span className={styles.livePulseRing} />
              <FaYoutube className={styles.btnIcon} />
              <span>Stream Live Now</span>
            </a>
          </div>
        </div>

        {/* Stats bar — count-up on load */}
        <div className={styles.statsBar}>
          {STATS.map((stat, i) => (
            <StatCounter
              key={i}
              index={i}
              target={stat.target}
              suffix={stat.suffix}
              label={stat.label}
              active={countActive}
            />
          ))}
        </div>

        {/* Scroll down cue */}
        <button
          className={styles.scrollCue}
          onClick={handleScrollDown}
          aria-label="Scroll down"
        >
          <FaChevronDown />
        </button>

      </section>
      <EventNotificationPopup />
    </>
  );
}